import {
    AsyncOpHandle,
    AsyncStatusOptions,
    AsyncOpStatus,
    isCompletedAsyncOp,
    CompletedAsyncOp,
    IDictionary,
    HttpVerb
} from 'util/domain';
import { CommonUtilsService } from 'util/services';

const defaultOptions: AsyncStatusOptions<CompletedAsyncOp, string> = {
    isComplete: isCompletedAsyncOp,
    interval: 500
};
const LOCATION_HEADER = 'location';

export class AsyncOpService {        
    public static readonly $inject = [
        '$q',
        '$http',
        '$timeout',
        'utils'
    ];

    public readonly handles:IDictionary<AsyncOpHandle<any, any>> = {};

    constructor(
        private readonly $q:ng.IQService,
        private readonly $http:ng.IHttpService,
        private readonly $timeout:ng.ITimeoutService,
        private readonly utils:CommonUtilsService
    ) {
    }

    /**
     * High level, syntax sugar method for making an async request that encapsulates
     * the established pattern of automatically checking the response for a location header 
     * and pinging until the server job is complete or fails.
     */
    public request<TResponse>(url:string, config?:ng.IRequestShortcutConfig, method:HttpVerb = 'POST'){
        return this
            .getAsyncResultUri(url, config, method)
            .then(uri => uri ? this.$http.get<TResponse>(uri).then(resp => resp.data) : this.$q.resolve());
    }

    public getAsyncResultUri(url:string, config?:ng.IRequestShortcutConfig, method:HttpVerb = 'POST'){
        let fullConfig:ng.IRequestConfig = {
            url,
            method
        };
        fullConfig = angular.merge(fullConfig, config);
        return this.$http(fullConfig)
            .then(res => res.headers(LOCATION_HEADER))
            .then(loc => this.getStatusUntilComplete(loc, {
                isComplete: status => status.IsComplete,
            }))
            .then(handle => {
                if(handle.cancelled){
                    throw new Error('Async request cancelled');
                }  

                return handle.status;
            })
            .then(resp => {
                // we're specifically checking for a literal value here instead of 
                // examining the object so that we can get an intelligent TypeScript 
                // return type based on the HTTP verb
                if(method === 'POST' || method === 'GET'){
                    return (resp as CompletedAsyncOp).Links.ResultUri;
                }
            });
    }

    /**
     * Continuously check the status of an asynchronous API operation until the operation completes. Callers must
     * provide a predicate that will be used to determine if the operation is done. The method returns an instance
     * of {@link AsyncOpHandle} that is bound to the requested operation.
     * 
     * @param location The location of the operation status
     * @param isComplete The predicate used to determine if the operation is complete
     * @param interval An optional interval, in milliseconds, to wait between API status calls. Defaults to 500 milliseconds
     * @param resultTransform An optional function used to transform the async operation result to a different type.
     */
    public getStatusUntilComplete<TResponse = AsyncOpStatus, TResult = TResponse>(location: string, options?: AsyncStatusOptions<TResponse, TResult>): AsyncOpHandle<TResponse, TResult> {
        const statusOptions = angular.merge({ }, defaultOptions, options) as AsyncStatusOptions<TResponse, TResult>;
        return this.createHandle(location, statusOptions);
    }

    private createHandle<TResponse = AsyncOpStatus, TResult = TResponse>(location, options?: AsyncStatusOptions<TResponse, TResult>){
        let handle = new AsyncOpHandle(this.$q, this.$http, this.$timeout, this.utils, location, options);
		this.handles[handle.id] = handle;
		handle.onComplete = () => this.removeHandle(handle);
        handle.onCancel = () => this.removeHandle(handle);
        return handle;
    }

    private removeHandle(h:AsyncOpHandle<any, any>){
		if(h.id in this.handles){
			this.handles[h.id] = null;
			delete this.handles[h.id];
		}
    }
}


// WEBPACK FOOTER //
// ./src/util/services/asyncOpService.ts